import _ from "lodash";
import matchDocumentClausepart from "./match_document_clausepart";
import {Clausepart, DocumentSection} from "common/types/document";
import {OtherDocumentClause} from "common/types/clause_preview";

const UNNUMBERED_MAIN_SECTION_LABEL = "REF";

interface FailedClauseTemplate {
  hasFailed: true;
  text: string;
  otherDocument?: OtherDocumentClause;
}

interface SuccessClauseTemplate {
  hasFailed?: false;
  text: string;
  isInUnnumberedSection?: boolean;
  data: {
    id;
    reference: Clausepart["reference"];
    clause_id?;
  };
}

export type ProcessedClauseTemplate =
  | FailedClauseTemplate
  | SuccessClauseTemplate;

export function parseClauseMarker(content: string) {
  const json = JSON.parse(content.trim());
  return {
    ...json,
    clause_reference: json.clause_reference ?? json.clause_references,
  };
}

// Marker-only read, no clausepart matching and no memo
export function parseOtherDocumentClause(
  content: string,
  documentId: number | undefined,
): OtherDocumentClause | undefined {
  if (documentId === undefined) {
    return undefined;
  }
  const json = parseClauseMarker(content);
  const markerDocumentId = getMarkerDocumentId(json);
  if (Number.isNaN(markerDocumentId) || markerDocumentId === documentId) {
    return undefined;
  }
  return toOtherDocumentClause(json, markerDocumentId);
}

// LLM output may quote the id
function getMarkerDocumentId(json): number {
  return json.document_id === undefined || json.document_id === null
    ? NaN
    : Number(json.document_id);
}

function toOtherDocumentClause(json, documentId: number): OtherDocumentClause {
  return _.omitBy(
    {
      document_id: documentId,
      section_reference: json.section_reference,
      clause_reference: json.clause_reference ?? json.clause_reference_general,
      clause_text: json.clause_text,
    },
    _.isUndefined,
  ) as OtherDocumentClause;
}

export function parseClauseObject(
  content,
  documentClauseparts: Clausepart[],
  documentSections: DocumentSection[],
  documentId?: number,
): ProcessedClauseTemplate {
  const json = parseClauseMarker(content);
  const fallbackText = `Clause ${
    json.clause_reference || json.clause_reference_general || "Unknown"
  }`.trim();

  const markerDocumentId = getMarkerDocumentId(json);
  if (
    documentId !== undefined &&
    !Number.isNaN(markerDocumentId) &&
    markerDocumentId !== documentId
  ) {
    return {
      hasFailed: true,
      text: fallbackText,
      otherDocument: toOtherDocumentClause(json, markerDocumentId),
    };
  }

  const clausepart = getMatch(json, documentClauseparts, documentSections);
  if (!clausepart) {
    return {hasFailed: true, text: fallbackText};
  }

  const section = documentSections.find(({id}) => id === clausepart.section_id);
  const text = getClauseLinkText(json, clausepart, section);

  return {
    hasFailed: false,
    text,
    isInUnnumberedSection: section?.is_unnumbered ?? false,
    data: _.pick(clausepart, ["id", "reference", "clause_id"]),
  };
}

function getClauseLinkText(
  clauseData: {clause_reference?: string},
  clausepart: Clausepart,
  section: DocumentSection | undefined,
): string {
  if (section && section.is_unnumbered) {
    if (section.reference === "main") {
      return UNNUMBERED_MAIN_SECTION_LABEL;
    }
    return section.reference;
  }

  const definedTerm = getDefinedTerm(clauseData.clause_reference);
  if (definedTerm) {
    return definedTerm;
  }
  const ref = clausepart.reference.replace(/^Clause\s*/i, "");
  if (section && section.reference !== "main") {
    return `${section.reference} - Clause ${ref}`;
  }
  return `Clause ${ref}`;
}

function getDefinedTerm(
  clauseReference: string | undefined,
): string | undefined {
  if (!clauseReference) {
    return undefined;
  }
  const match = clauseReference.match(/^\d+\.(.+)$/);
  if (!match) {
    return undefined;
  }
  const tail = match[1];
  if (/[A-Z][a-z]/.test(tail) || /\s/.test(tail)) {
    return `Definition: ${tail}`;
  }
  return undefined;
}

function getMatch(
  clauseData,
  documentClauseparts: Clausepart[],
  documentSections: DocumentSection[],
): Clausepart | null {
  const clausepartIdMatch =
    clauseData.id === undefined
      ? undefined
      : documentClauseparts.find(({id}) => id === clauseData.id);
  if (clausepartIdMatch) {
    return clausepartIdMatch;
  }

  const section = documentSections.find(
    ({reference}) => reference === clauseData.section_reference,
  );
  if (!section) {
    return null;
  }

  const documentSectionClauseparts = documentClauseparts.filter(
    ({section_id}) => section.id === section_id,
  );
  const clauseMatch = matchDocumentClausepart(
    clauseData,
    documentSectionClauseparts,
  );
  if (!clauseMatch) {
    return null;
  }

  return clauseMatch;
}
